import { Add, Close, Delete, ErrorOutline, ExpandLess, ExpandMore, Redo } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, IconButton, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';

import { useI18n } from '../../../application/language/useI18n';
import { ITaskLanguage } from '../../../service/unit/interface/IUnitPractice';
import { CodeContextMenu } from './parts/contextMenu/CodeContextMenu';
import { FileTree } from './parts/fileTree/FileTree';
import { ReviewEditor } from './parts/review/ReviewEditor';
import { CodeEditorDomain } from './store/CodeEditorDomain';
import { CodeMode } from './store/ICodeMode';
import { Colors } from '../color/Colors';

export interface ICodeEditorProperties {
  lang: ITaskLanguage;
  isExpand: boolean;
  handleExpand: (isExpand: boolean) => void;
}

export const CodeEditor = observer(({ lang, isExpand, handleExpand }: ICodeEditorProperties) => {
  const { translate } = useI18n();
  const [domain] = useState<CodeEditorDomain>(new CodeEditorDomain());
  const contentRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState<number>(0);

  useEffect(() => {
    if (lang !== null) {
      domain.ui.fileTree.setList(lang.files);
      domain.ui.lang.setValue(lang);
      domain.ui.currentFile.setValue(null as any);
    }
  }, [domain.ui.fileTree, domain.ui.lang, lang]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const observer = new (window as any).ResizeObserver((entries: any[]) => {
      for (const entry of entries) {
        setEditorHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const file = domain.ui.currentFile.value;
  const isFileSelected = file !== null && !file?.hasOwnProperty('children');

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${Colors.border}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Top toolbar — undo/redo, reset, expand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 16px',
          borderBottom: `1px solid ${Colors.border}`,
          borderTop: `1px solid ${Colors.border}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            background: Colors.bgSurface,
            marginRight: '16px',
            borderRadius: '8px',
            display: 'flex',
          }}
        >
          <IconButton size="small">
            <Redo sx={{ transform: ' scale(-1, 1) ' }} />
          </IconButton>
          <Divider orientation="vertical" />
          <IconButton size="small">
            <Redo />
          </IconButton>
        </Box>
        <Button
          color="secondary"
          size="small"
          sx={{ background: Colors.bgSurface, borderRadius: '8px', textTransform: 'none', marginRight: 'auto' }}
        >
          {translate('phrases.resetChanges')}
        </Button>
        <Tooltip title={!isExpand ? translate('phrases.expand') : translate('phrases.fold')}>
          <IconButton
            onClick={() => handleExpand(!isExpand)}
            sx={{
              marginLeft: '8px',
              alignSelf: 'center',
              height: '24px',
              width: '24px',
              background: Colors.textSecondary,
              borderRadius: '4px',
              color: Colors.textOnDark,
              '&:hover': {
                background: Colors.primaryActive,
                color: Colors.textPrimary,
              },
            }}
          >
            {!isExpand && <ExpandLess />}
            {isExpand && <ExpandMore />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main content area */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* File tree sidebar */}
        <Box
          sx={{
            minWidth: '218px',
            overflowX: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            maxWidth: 'fit-content',
            borderRight: `1px solid ${Colors.border}`,
            overflowY: 'auto',
          }}
        >
          <Typography sx={{ padding: '4px 8px' }}>{translate('phrases.project')}</Typography>
          <FileTree
            openContextMenu={domain.openContextMenu}
            fileTree={domain.ui.fileTree.list}
            pickingFile={domain.pickingFile}
            isInputFileOpen={domain.ui.isInputFileOpen}
            isInputFolderOpen={domain.ui.isInputFolderOpen}
            isInputRenameOpen={domain.ui.isInputRenameOpen}
            creatingNewFolder={domain.creatingNewFolder}
            creatingNewFile={domain.creatingNewFile}
            currentFile={domain.ui.currentFile.value}
            toggleQuestion={domain.toggleQuestion}
            rename={domain.rename}
          />
          {isFileSelected && (
            <Box
              onClick={(event) => {
                event.preventDefault();
                domain.toggleQuestion(domain.ui.currentFile.value);
              }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                gap: '9.5px',
                fontSize: '16px',
                color: Colors.primary,
                marginLeft: '16px',
                marginBottom: '16px',
              }}
            >
              {file.isQuestion === false ? (
                <>
                  <ErrorOutline />
                  <Typography>{translate('codeEditor.turnIntoQuestion')}</Typography>
                </>
              ) : (
                <>
                  <Close />
                  <Typography>{translate('codeEditor.removeQuestion')}</Typography>
                </>
              )}
            </Box>
          )}
          {domain.ui.isContextOpen.value && (
            <CodeContextMenu
              currentFile={domain.ui.currentFile.value}
              newFolder={domain.newFolder}
              newFile={domain.newFile}
              open={domain.ui.isContextOpen.value}
              onClose={domain.closeContextMenu}
              onDelete={domain.onDelete}
              onRename={domain.onRename}
              toggleQuestion={domain.toggleQuestion}
              onDuplicate={domain.onDuplicate}
              // @ts-ignore
              event={domain.ui.event?.value}
            />
          )}
        </Box>

        {/* Editor area — header + two editors */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Unified editor header — variant controls when file is a question */}
          {isFileSelected && file.isQuestion && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '4px 8px',
                borderBottom: `1px solid ${Colors.border}`,
                flexShrink: 0,
                gap: '4px',
              }}
            >
              {lang.options?.map((option) => (
                <Button
                  key={option.name}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1.5,
                    background: option.name === domain.ui.currentOption.value?.name
                      ? (option.isCorrect ? Colors.bgCorrectActive : Colors.bgSurface)
                      : (option.isCorrect ? Colors.bgCorrect : ''),
                    border: option.isCorrect ? `1px solid ${Colors.success}` : '',
                  }}
                  onClick={() => domain.pickingOption(option)}
                >
                  {option.name}
                </Button>
              ))}
              <IconButton
                size="small"
                onClick={() => domain.addOption(lang)}
              >
                <Add fontSize="small" />
              </IconButton>
              {domain.ui.currentOption.value !== null && (
                <IconButton
                  size="small"
                  onClick={() => domain.deleteOption(domain.ui.currentOption.value, lang)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
              <FormGroup sx={{ ml: 'auto' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={domain.ui.currentOption.value !== null ? domain.ui.currentOption.value.isCorrect : false}
                      size="small"
                      onChange={() => {
                        if (domain.ui.currentOption.value) {
                          domain.toggleOption(domain.ui.currentOption.value.name);
                        }
                      }}
                    />
                  }
                  label={translate('phrases.correct')}
                  sx={{ mr: 0 }}
                />
              </FormGroup>
            </Box>
          )}

          {/* Two editors side by side */}
          <Box ref={contentRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {domain.ui.mode.value === CodeMode.review && lang !== null ? (
              <ReviewEditor
                lang={lang}
                file={domain.ui.currentFile.value}
                currentOption={domain.ui.currentOption}
                height={editorHeight}
              />
            ) : (
              <Typography sx={{ padding: '4px 8px' }}>Untitled</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
